
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, UserCheck, Calendar, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';

const PatientManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const patients = [
    {
      id: '1',
      name: 'Sarah Johnson',
      email: 'sarah.j@email.com',
      phone: '(555) 123-4567',
      status: 'active',
      lastVisit: '2024-01-15',
      creditBalance: 450,
      totalVisits: 8
    },
    {
      id: '2', 
      name: 'Mike Chen',
      email: 'mike.chen@email.com',
      phone: '(555) 987-6543',
      status: 'active',
      lastVisit: '2024-01-10',
      creditBalance: 0,
      totalVisits: 3
    },
    {
      id: '3',
      name: 'Lisa Wong',
      email: 'lisa.wong@email.com', 
      phone: '(555) 456-7890',
      status: 'inactive',
      lastVisit: '2023-12-20',
      creditBalance: 200,
      totalVisits: 12
    }
  ];

  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-juv-cream to-juv-sage/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold font-juv-heading text-juv-deep-chestnut">
                Patient Management
              </h1>
              <p className="mt-1 text-juv-deep-chestnut/70">
                Search and manage patient records
              </p>
            </div>
            <Button className="bg-juv-terracotta text-white">
              <Plus className="h-4 w-4 mr-2" />
              Add New Patient
            </Button>
          </div>

          {/* Search */}
          <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
            <CardContent className="p-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search patients by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Patient List */}
          <div className="grid gap-4">
            {filteredPatients.map((patient) => (
              <Card key={patient.id} className="bg-white shadow-luxury border border-juv-rose-clay/30 hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-3">
                        <h3 className="text-lg font-semibold text-juv-deep-chestnut">
                          {patient.name}
                        </h3>
                        <Badge className={getStatusColor(patient.status)}>
                          {patient.status}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Email:</span>
                          <p>{patient.email}</p>
                        </div>
                        <div>
                          <span className="font-medium">Phone:</span>
                          <p>{patient.phone}</p>
                        </div>
                        <div>
                          <span className="font-medium">Last Visit:</span>
                          <p>{new Date(patient.lastVisit).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <span className="font-medium">Total Visits:</span>
                          <p>{patient.totalVisits}</p>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center space-x-6">
                        <div className="flex items-center space-x-2">
                          <CreditCard className="h-4 w-4 text-juv-terracotta" />
                          <span className="text-sm font-medium">
                            Credits: ${patient.creditBalance}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/staff/patients/${patient.id}/appointments`}>
                          <Calendar className="h-4 w-4 mr-1" />
                          Appointments
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/staff/patients/${patient.id}/credits`}>
                          <CreditCard className="h-4 w-4 mr-1" />
                          Credits
                        </Link>
                      </Button>
                      <Button asChild size="sm" className="bg-juv-terracotta text-white">
                        <Link to={`/staff/patients/${patient.id}`}>
                          <UserCheck className="h-4 w-4 mr-1" />
                          View Profile
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientManagement;
